import React, { useEffect } from 'react';
import { useSelectorMachineContext, useSelectorMachineSend } from '@printdeal/product-selector';
import { useExperiment } from '@printdeal/feature-management/react';
import { isCustomerProgramReseller, useSegmentation } from '@printdeal/segmentation/react';
import { FileDeliveryAndServiceProps } from './file-delivery-and-service-types';
import { FileDeliveryAndServicesExperiment } from './FileDeliveryAndServicesExperiment';
import { FileDeliveryAndServiceSelection } from './FileDeliveryAndServiceSelection';
import type { SelectedDeliveryTool } from '../../../../../types/file-delivery/FileDelivery';

export const FileDeliveryAndServiceWrapper = ({
  productPage,
  selector,
  services,
  setSelectedDeliveryTool,
  setHasDeliveryToolChoice,
  selectedServices,
  alternativeUploadService,
  setAlternativeUploadService,
  setSelectedServices,
  selectedDeliveryTool,
  hasFileDeliveryChoice,
  isChoiceSkipped,
  setIsChoiceSkipped,
  isSelectionComplete,
  isDirectUploadConfigured,
  deliveryTool,
  hadSelectionOnPageLoad = false,
}: FileDeliveryAndServiceProps) => {
  const {
    experiment: exp1862, isInitialized: isExp1862Initialized,
  } = useExperiment('servicesprouts_exp1862_rerun_explicit_choice_filecheck');
  const segmentation = useSegmentation();
  const isReseller = isCustomerProgramReseller(
    segmentation.data?.isReseller,
    segmentation.data?.isPartOfCustomerProgram,
  );
  const isExp1862Variant = Boolean(isExp1862Initialized && exp1862?.variation === 'variant') && !isReseller;
  const selectedServiceInMachine = useSelectorMachineContext((state) => state.context.selectedService);
  const send = useSelectorMachineSend();

  useEffect(() => {
    if (isSelectionComplete && !isDirectUploadConfigured && deliveryTool && setHasDeliveryToolChoice) {
      const choices = Object.entries(deliveryTool).filter(([, settingValue]) => settingValue === 'on');
      switch (choices.length) {
        case 0:
          setHasDeliveryToolChoice(false);
          break;
        case 1: {
          if (!isExp1862Variant) {
            const [choice] = choices;
            const [tool] = choice;
            const selectedTool = tool === 'altuploadbeforeorder' ? 'alternative-upload' : tool;
            setSelectedDeliveryTool(selectedTool as SelectedDeliveryTool);
          }
          setHasDeliveryToolChoice(true);
          break;
        }
        default:
          setHasDeliveryToolChoice(true);
          break;
      }
    }
  }, [
    isSelectionComplete,
    deliveryTool,
    isDirectUploadConfigured,
    isExp1862Variant,
    setHasDeliveryToolChoice,
    setSelectedDeliveryTool,
  ]);

  useEffect(() => {
    if ((isExp1862Variant || isDirectUploadConfigured) && setAlternativeUploadService && !hadSelectionOnPageLoad) {
      setAlternativeUploadService(undefined);
    }

    if (!hadSelectionOnPageLoad) {
      setSelectedDeliveryTool(undefined);
    }

    setIsChoiceSkipped(false);

    // Remove no-longer-valid selectedServices from both the selector stateMachine AND local state-management.
    if (!deliveryTool?.servicekeys) {
      // No services are valid, commence straight cleanup.
      if (selectedServiceInMachine) {
        send({ type: 'Select service', selectedService: '' });
      }
      if (selectedServices.length) {
        setSelectedServices([]);
      }
    } else {
      // Service might still be valid. Verify and cleanup.
      const validServiceKeys = deliveryTool.servicekeys?.split(',');
      if (selectedServiceInMachine && !validServiceKeys.includes(selectedServiceInMachine)) {
        send({ type: 'Select service', selectedService: '' });
      }
      setSelectedServices(selectedServices
        .filter((service) => validServiceKeys
          .find((validServiceKey) => validServiceKey.toLowerCase() === service.toLowerCase())));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryTool?.servicekeys, isExp1862Variant]);

  if (isExp1862Variant) {
    return (
      <FileDeliveryAndServicesExperiment
        productPage={productPage}
        selector={selector}
        services={services}
        setSelectedDeliveryTool={setSelectedDeliveryTool}
        setHasDeliveryToolChoice={setHasDeliveryToolChoice}
        selectedServices={selectedServices}
        alternativeUploadService={alternativeUploadService}
        setAlternativeUploadService={setAlternativeUploadService}
        setSelectedServices={setSelectedServices}
        selectedDeliveryTool={selectedDeliveryTool}
        hasFileDeliveryChoice={hasFileDeliveryChoice}
        isChoiceSkipped={isChoiceSkipped}
        setIsChoiceSkipped={setIsChoiceSkipped}
        isExp1862Variant={isExp1862Variant}
      />
    );
  }

  return (
    <FileDeliveryAndServiceSelection
      productPage={productPage}
      selector={selector}
      hasFileDeliveryChoice={hasFileDeliveryChoice}
      setSelectedDeliveryTool={setSelectedDeliveryTool}
      selectedDeliveryTool={selectedDeliveryTool}
      services={services}
      setSelectedServices={setSelectedServices}
      selectedServices={selectedServices}
      setIsChoiceSkipped={setIsChoiceSkipped}
    />
  );
};
